import type { HotelSearchResultItem, HotelSearchSavedItemData } from 'tripPlanner/api/types'
import {
  displayPricingFromDTO,
  includesTaxesAndFees,
  isTotalMode,
  pricingUnavailableReason,
  type PublicAccommodationPricing,
} from 'tripPlanner/utils/accommodationPricingDisplay'
import type {
  DrawerHotel,
  DrawerHotelGeoLocation,
  DrawerHotelPrice,
} from './types'

const DEFAULT_NIGHTS = 1

export function composeLocationString(
  city?: string,
  country?: string,
): string {
  return [city, country].filter(Boolean).join(', ')
}

export function mapImages(imageIds: Array<string>): Array<App.Image> {
  return imageIds.map((id) => ({ id }))
}

export function mapGeoLocation(
  geoLocation?: HotelSearchSavedItemData['location']['geoLocation'],
): DrawerHotelGeoLocation | undefined {
  if (!geoLocation) {
    return undefined
  }

  return {
    lat: geoLocation.lat,
    lng: geoLocation.lng,
  }
}

export function mapPrice(
  price?: number,
  numberOfNights?: number,
  currencyCode?: string,
): DrawerHotelPrice | undefined {
  if (price === undefined || price <= 0) {
    return undefined
  }

  return {
    total: price,
    nights: numberOfNights ?? DEFAULT_NIGHTS,
    currency: currencyCode,
  }
}

export function mapDrawerHotelPricingFields(
  pricing: PublicAccommodationPricing | undefined,
  nights?: number,
  currencyCode?: string,
): Pick<
  DrawerHotel,
  | 'price'
  | 'isTotal'
  | 'includesTaxesAndFees'
  | 'unavailableReason'
  | 'luxPlusPrice'
  | 'value'
  | 'memberValue'
  | 'discountPercent'
  | 'memberDiscountPercent'
> {
  const displayPricing = pricing ? displayPricingFromDTO(pricing) : undefined

  return {
    price: mapPrice(displayPricing?.price, nights, displayPricing?.currencyCode ?? currencyCode),
    isTotal: pricing ? isTotalMode(pricing.mode) : false,
    includesTaxesAndFees: pricing ? includesTaxesAndFees(pricing) : false,
    unavailableReason: pricingUnavailableReason(pricing),
    luxPlusPrice: displayPricing?.luxPlusPrice,
    value: displayPricing?.value,
    memberValue: displayPricing?.memberValue,
    discountPercent: displayPricing?.discountPercent,
    memberDiscountPercent: displayPricing?.memberDiscountPercent,
  }
}

export function mapToDrawerHotel(item: HotelSearchResultItem): DrawerHotel | undefined {
  const { savedItemData } = item

  if (!savedItemData?.imageIds?.length) {
    return undefined
  }

  const { location } = savedItemData
  const nights = savedItemData.pricing?.durationNights ?? item.numberOfNights

  return {
    id: savedItemData.offerId,
    offerId: savedItemData.offerId,
    offerType: savedItemData.offerType,
    slug: item.slug,
    name: item.name,
    description: item.description || undefined,
    location: composeLocationString(location.city, location.country),
    images: mapImages(savedItemData.imageIds),
    rating: savedItemData.rating,
    ...mapDrawerHotelPricingFields(savedItemData.pricing, nights, savedItemData.currencyCode),
    geoLocation: mapGeoLocation(location.geoLocation),
    deeplinkUrl: item.deeplinkUrl,
    checkIn: savedItemData.checkIn,
    checkOut: savedItemData.checkOut,
  }
}
